import { Component, Input, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicFormComponent } from 'src/app/shared/components/dynamic-form/dynamic-form.component';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { GoogleRecaptchaService } from 'src/app/shared/services/google-recaptcha/google-recaptcha.service';

declare const Helpers: any;

const LITMUSWORLD_DOMAIN = 'litmusworld.com';
const EMAIL_PATTERN = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$';
const INVALID_DOMAIN_MESSAGE = 'Please enter a valid LitmusWorld Email address';

type ValidationResult = { valid: boolean; message?: string };

@Component({
  selector: 'app-add-admin',
  templateUrl: './add-admin.component.html',
  styleUrls: ['./add-admin.component.scss'],
  standalone: true,
  imports: [CommonModule, DynamicFormComponent]
})
export class AddAdminComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm?: DynamicFormComponent;
  @Input() accountId: string = '';
  @Input() canWrite: boolean = false;
  @Output() adminCreated = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();

  formSchema: any = null;
  isSubmitting: boolean = false;
  recaptchaToken: string | null = null;
  isSelfServiceUser: boolean = false;

  constructor(
    private accountsService: AccountsService,
    private appStateService: AppStateService,
    private toast: ToastService,
    private grecaptcha: GoogleRecaptchaService
  ) {}

  ngOnInit(): void {
    this.isSelfServiceUser = this.checkIfSelfServiceUser();
    this.buildFormSchema();
    this.initializeRecaptcha();
  }

  private checkIfSelfServiceUser(): boolean {
    if (typeof Helpers !== 'undefined' && typeof Helpers.isSelfServiceUser === 'function') {
      return !!Helpers.isSelfServiceUser();
    }
    return false;
  }

  private buildFormSchema(): void {
    this.formSchema = {
      type: 'object',
      properties: {
        first_name: {
          label: 'First Name',
          type: 'string',
          minLength: 2,
          maxLength: 50,
          placeholder: 'Enter first name',
          customValidation: (value: string) => this.validateName(value, 'First name'),
          errorMessages: {
            required: 'First name is required',
            minLength: 'First name must be at least 2 characters',
            maxLength: 'First name cannot exceed 50 characters'
          }
        },
        last_name: {
          label: 'Last Name',
          type: 'string',
          minLength: 2,
          maxLength: 50,
          placeholder: 'Enter last name',
          customValidation: (value: string) => this.validateName(value, 'Last name'),
          errorMessages: {
            required: 'Last name is required',
            minLength: 'Last name must be at least 2 characters',
            maxLength: 'Last name cannot exceed 50 characters'
          }
        },
        email_address: {
          label: 'Email Address',
          type: 'string',
          pattern: EMAIL_PATTERN,
          placeholder: this.isSelfServiceUser ? 'Enter a valid email address' : INVALID_DOMAIN_MESSAGE,
          customValidation: (value: string) => this.validateEmail(value),
          errorMessages: {
            required: 'Email address is required',
            pattern: 'Please enter a valid email address'
          }
        },
        phone_number: {
          label: 'Phone Number',
          type: 'string',
          placeholder: 'Enter phone number (optional)',
          customValidation: (value: string) => this.validatePhone(value)
        },
        is_self_service_user: {
          label: 'Self Service Admin',
          type: 'boolean',
          input: 'checkbox',
          default: false
        }
      },
      required: ['first_name', 'last_name', 'email_address']
    };

    // A self service admin can only ever create other self service admins.
    if (this.isSelfServiceUser) {
      this.formSchema.required.push('is_self_service_user');
      this.formSchema.properties.is_self_service_user.errorMessages = {
        required: 'Please tick Self service user checkbox'
      };
    }
  }

  private validateName(value: string, label: string): ValidationResult {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      // Empty is reported by the required validator; only reject whitespace-only input here.
      return value ? { valid: false, message: `${label} is required` } : { valid: true };
    }
    if (trimmed.length < 2) {
      return { valid: false, message: `${label} must be at least 2 characters` };
    }
    if (trimmed.length > 50) {
      return { valid: false, message: `${label} cannot exceed 50 characters` };
    }
    return { valid: true };
  }

  private validateEmail(value: string): ValidationResult {
    const email = (value || '').trim();
    if (!email) {
      return { valid: true };
    }
    if (!new RegExp(EMAIL_PATTERN).test(email)) {
      return { valid: false, message: 'Please enter a valid email address' };
    }
    if (!this.isSelfServiceUser && this.domainOf(email) !== LITMUSWORLD_DOMAIN) {
      return { valid: false, message: INVALID_DOMAIN_MESSAGE };
    }
    return { valid: true };
  }

  private validatePhone(value: string): ValidationResult {
    const phone = (value || '').trim();
    if (!phone) {
      return { valid: true };
    }
    if (!/^\+?[0-9\s()-]+$/.test(phone)) {
      return { valid: false, message: 'Phone number can only contain digits, spaces, brackets, + and -' };
    }
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 7 || digits.length > 15) {
      return { valid: false, message: 'Phone number must be between 7 and 15 digits' };
    }
    return { valid: true };
  }

  private domainOf(email: string): string {
    const separator = (email || '').lastIndexOf('@');
    return separator === -1 ? '' : email.slice(separator + 1).trim().toLowerCase();
  }

  private initializeRecaptcha(): void {
    this.grecaptcha.initializeToken().subscribe(token => {
      this.recaptchaToken = token;
    });
  }

  onFormSubmit(formValue: any): void {
    if (this.isSubmitting) return;

    if (!this.canWrite) {
      this.toast.showPopup('ERROR', 'Access Denied', 'You do not have write permission to add admins. Only read access is available.', 4000);
      return;
    }

    const firstName = (formValue.first_name || '').trim();
    const lastName = (formValue.last_name || '').trim();
    const emailAddress = (formValue.email_address || '').trim();
    const phoneNumber = (formValue.phone_number || '').trim();
    // The dynamic form keys controls off the visible label, so accept either spelling.
    const isSelfServiceAdmin = !!(formValue.is_self_service_user ?? formValue.self_service_admin);

    const nameCheck = this.validateName(firstName, 'First name');
    if (!nameCheck.valid) {
      this.toast.showPopup('ERROR', 'Invalid Name', nameCheck.message!, 3000);
      return;
    }
    const lastNameCheck = this.validateName(lastName, 'Last name');
    if (!lastNameCheck.valid) {
      this.toast.showPopup('ERROR', 'Invalid Name', lastNameCheck.message!, 3000);
      return;
    }

    const emailCheck = this.validateEmail(emailAddress);
    if (!emailCheck.valid) {
      this.toast.showPopup('ERROR', 'Invalid Email', emailCheck.message!, 3000);
      return;
    }

    const phoneCheck = this.validatePhone(phoneNumber);
    if (!phoneCheck.valid) {
      this.toast.showPopup('ERROR', 'Invalid Phone Number', phoneCheck.message!, 3000);
      return;
    }

    // A self service admin must tick the checkbox for every admin they create.
    if (this.isSelfServiceUser && !isSelfServiceAdmin) {
      this.toast.showPopup('ERROR', 'Error', 'Please tick Self service user checkbox', 3000);
      return;
    }

    // LitmusWorld users cannot be self service users.
    if (this.domainOf(emailAddress) === LITMUSWORLD_DOMAIN && isSelfServiceAdmin) {
      this.toast.showPopup('WARN', 'Warning', 'LitmusWorld user cannot be a self service user. Do not mark the checkbox.', 3000);
      return;
    }

    const accountId = this.resolveAccountId();
    if (!accountId) {
      this.toast.showPopup('ERROR', 'Account Error', 'Account ID is required. Please refresh and try again.', 3000);
      return;
    }

    const managerDetails = this.buildManagerPayload({
      accountId,
      firstName,
      lastName,
      emailAddress,
      phoneNumber,
      isSelfServiceAdmin
    });

    if (!this.recaptchaToken) {
      this.grecaptcha.initializeToken().subscribe(token => {
        if (token) {
          this.recaptchaToken = token;
          this.createAdmin(managerDetails);
        } else {
          this.toast.showPopup('ERROR', 'reCAPTCHA Required', 'Please complete the reCAPTCHA verification and try again.', 3000);
        }
      });
      return;
    }

    this.createAdmin(managerDetails);
  }

  private resolveAccountId(): string {
    if (this.accountId) {
      return this.accountId;
    }
    const accountDetails: any = this.appStateService.accountDetails();
    return accountDetails?._id || localStorage.getItem('LtMsAcTiDfR') || '';
  }

  private buildManagerPayload(details: {
    accountId: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
    isSelfServiceAdmin: boolean;
  }): any {
    const managerDetails: any = {
      account_id: details.accountId,
      first_name: details.firstName,
      last_name: details.lastName,
      email_address: details.emailAddress,
      user_name: details.emailAddress,
      phone_number: details.phoneNumber,
      manager_type: 3, // Account manager type
      version: 'v3',
      is_self_service_user: details.isSelfServiceAdmin
    };

    if (this.isSelfServiceUser || details.isSelfServiceAdmin) {
      managerDetails.self_service_user_type = 'gold';
    }

    return managerDetails;
  }

  private createAdmin(managerDetails: any): void {
    this.isSubmitting = true;
    managerDetails.g_recaptcha_response_v3 = this.recaptchaToken;

    this.accountsService.addAccountManager(managerDetails).subscribe({
      next: (response) => {
        this.isSubmitting = false;

        if (response && response.code === 0) {
          this.toast.showPopup('SUCCESS', 'Success', 'Account manager has been added successfully', 3000);
          this.adminCreated.emit(response);
          this.dynamicForm?.onReset();
        } else if (response.code === 401) {
          const message = this.isSelfServiceUser
            ? "Existing manager's cannot be created as self service user"
            : 'Account manager already exists';
          this.toast.showPopup('WARN', 'Warning', message, 3000);
        } else if (response.code === 2107) {
          this.toast.showPopup('WARN', 'Warning', 'User account have deactivated.', 3000);
        } else if (response.code === 406) {
          this.toast.showPopup('WARN', 'Warning', 'Manager already exists, but not verified', 3000);
        } else if (response.code === 403) {
          this.toast.showPopup('WARN', 'Warning', 'Authorization failed', 3000);
        } else {
          this.toast.showPopup('ERROR', 'Error', 'Error while giving account access, please contact system administrator.', 3000);
        }

        this.refreshRecaptcha();
      },
      error: (error) => {
        this.isSubmitting = false;
        console.error('Error creating admin:', error);
        this.toast.showPopup('ERROR', 'Error', 'An error occurred while creating the admin. Please try again.', 3000);
        this.refreshRecaptcha();
      }
    });
  }

  /** A v3 token is single use, so a fresh one is needed before the next attempt. */
  private refreshRecaptcha(): void {
    this.recaptchaToken = null;
    this.initializeRecaptcha();
  }

  /** Triggered by the panel footer so the actions can stay pinned outside the form. */
  submit(): void {
    this.dynamicForm?.onSubmit();
  }

  canSubmit(): boolean {
    return this.canWrite && !this.isSubmitting && !!this.dynamicForm?.isFormReady();
  }

  onCancel(): void {
    this.cancel.emit();
  }
}
